// UnlockModal - Paywall modal with dynamic content based on user status
// Security: No PII displayed, safe navigation

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserStatus, createTripUnlockCheckout, createSubscriptionCheckout } from '../services/api';
import { logger } from '../utils/logger';
import { savePendingCheckout, isCheckoutInFlight } from '../utils/pendingCheckout';
import AuthModal from './AuthModal';
import './UnlockModal.css';

export type UnlockTrigger = 'chat' | 'pdf' | 'banner' | 'customize' | 'signin';
export type UnlockEntrypoint = 'cta' | 'edit_action' | 'export' | 'customize';

interface UnlockModalProps {
    isOpen: boolean;
    onClose: () => void;
    userStatus: UserStatus | null;
    tripSlug?: string;
    tripId?: string;
    trigger?: UnlockTrigger;
    entrypoint?: UnlockEntrypoint;
    forceUpgradeView?: boolean;
    checkoutCanceled?: boolean;
    sessionExpired?: boolean;
}

export default function UnlockModal({ 
    isOpen, 
    onClose, 
    userStatus, 
    tripSlug = '', 
    tripId = '',
    trigger = 'chat',
    entrypoint = 'cta',
    forceUpgradeView = false,
    checkoutCanceled = false,
    sessionExpired = false,
}: UnlockModalProps) {
    const { t } = useTranslation();
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [view, setView] = useState<'trial' | 'upgrade' | 'promo'>('trial');
    const [plan, setPlan] = useState<'trip' | 'annual'>('trip'); // Default to trip for promo
    const [trialMessage, setTrialMessage] = useState<string | null>(null);

    // Get entrypoint-aware headline
    const getHeadline = () => {
        if (forceUpgradeView) {
            switch (entrypoint) {
                case 'cta':
                case 'customize':
                    return t('unlock.headline_edit_export');
                case 'export':
                    return t('unlock.headline_export');
                case 'edit_action':
                    return t('unlock.headline_edit');
                default:
                    return t('unlock.headline_default');
            }
        }
        return t('unlock.unlock_unlimited');
    };

    // Track paywall view and initialize state once per open
    useEffect(() => {
        if (isOpen) {
            // Reset state
            setTrialMessage(null);

            // Determine initial view
            // Always show promo view for trip unlock scenarios (has tripSlug)
            // Only show trial view if user has free edits and we're not forcing upgrade
            if (forceUpgradeView || tripSlug) {
                setView('promo');
                setPlan('trip');
            } else if (!userStatus?.freeEditsRemaining || userStatus.freeEditsRemaining <= 0) {
                setView('promo');
                setPlan('annual');
            } else {
                setView('trial');
                setPlan('annual');
            }

            // Plausible tracking
            (window as any).plausible?.('paywall_view', { trigger, entrypoint, forceUpgradeView });
        }
    }, [isOpen, trigger, entrypoint, forceUpgradeView, userStatus?.freeEditsRemaining]);

    if (!isOpen) return null;

    // Handle "Continue with Google to unlock" - save pending checkout and redirect
    const handleGoogleUnlock = () => {
        if (isCheckoutInFlight()) return;
        
        // Save pending checkout before OAuth redirect
        savePendingCheckout({
            tripId,
            tripSlug,
            priceId: '', // Will be resolved server-side
            plan: 'trip_unlock',
            returnTo: `/trip/${tripSlug}`,
        });

        // Track and redirect to Google OAuth
        (window as any).plausible?.('google_unlock_start', { entrypoint });
        window.location.href = `/api/auth/google?redirect=${encodeURIComponent(`/trip/${tripSlug}`)}`;
    };

    // If auth modal is open, render it instead (for non-promo flow)
    if (showAuthModal) {
        return (
            <AuthModal
                isOpen={true}
                onClose={() => setShowAuthModal(false)}
                redirectPath={`/trip/${tripSlug}`}
            />
        );
    }

    const handleCheckout = async () => {
        if (checkoutLoading) return;
        setCheckoutLoading(true);

        // Track checkout start
        (window as any).plausible?.('checkout_start', { trigger, plan });

        try {
            const apiCall = plan === 'annual'
                ? createSubscriptionCheckout('annual')
                : createTripUnlockCheckout(tripSlug);
            const result = await apiCall;
            if (result.sessionUrl) {
                window.location.href = result.sessionUrl;
            }
        } catch (err) {
            logger.error('Checkout failed:', err);
            alert(t('chat.connection_failed'));
            setCheckoutLoading(false); // Re-enable button on error
        }
    };

    const handleUpgradeClick = () => {
        setView('upgrade');
        (window as any).plausible?.('upgrade_click', { trigger });
    };

    const handlePlanSelect = (newPlan: 'trip' | 'annual') => {
        setPlan(newPlan);
        (window as any).plausible?.('plan_select', { trigger, plan: newPlan });
    };

    const handleContinueFree = () => {
        if (trigger === 'pdf') {
            setTrialMessage(t('unlock.pdf_premium_notice'));
            return;
        }

        (window as any).plausible?.('continue_free_click', { trigger, editsRemaining: userStatus?.freeEditsRemaining || 0 });
        onClose();
    };

    return (
        <div className="unlock-modal-overlay" onClick={onClose}>
            <div className="unlock-modal" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={onClose} aria-label={t('common.close')}>
                    <CloseIcon />
                </button>

                <div className="modal-icon">
                    <LockIcon />
                </div>

                {/* Promo view (forceUpgradeView) - Mobile CTA flow */}
                {view === 'promo' ? (
                    <PromoContent
                        t={t}
                        headline={getHeadline()}
                        isAuthenticated={!!userStatus?.isAuthenticated}
                        isEligibleForLaunchPromo={!!userStatus?.isEligibleForLaunchPromo}
                        onGoogleUnlock={handleGoogleUnlock}
                        onCheckout={handleCheckout}
                        onClose={onClose}
                        loading={checkoutLoading}
                        checkoutCanceled={checkoutCanceled}
                        sessionExpired={sessionExpired}
                    />
                ) : !userStatus?.isAuthenticated ? (
                    /* State 1: Unauthenticated (legacy flow) */
                    <GuestContent t={t} onGetStarted={() => setShowAuthModal(true)} />
                ) : view === 'trial' ? (
                    /* State 2: Free trial with edits remaining */
                    <FreeTrialContent
                        t={t}
                        editsRemaining={userStatus.freeEditsRemaining}
                        onUpgradeClick={handleUpgradeClick}
                        onContinueFree={handleContinueFree}
                        trialMessage={trialMessage}
                    />
                ) : (
                    /* State 3: Upgrade view */
                    <UpgradeContent
                        t={t}
                        plan={plan}
                        onPlanSelect={handlePlanSelect}
                        onCheckout={handleCheckout}
                        loading={checkoutLoading}
                        isTripUnlocked={!!userStatus?.isTripUnlocked}
                        hasTripContext={!!tripSlug}
                    />
                )}
            </div>
        </div>
    );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function PromoContent({
    t,
    headline,
    isAuthenticated,
    isEligibleForLaunchPromo,
    onGoogleUnlock,
    onCheckout,
    onClose,
    loading,
    checkoutCanceled,
    sessionExpired,
}: {
    t: any;
    headline: string;
    isAuthenticated: boolean;
    isEligibleForLaunchPromo: boolean;
    onGoogleUnlock: () => void;
    onCheckout: () => void;
    onClose: () => void;
    loading: boolean;
    checkoutCanceled?: boolean;
    sessionExpired?: boolean;
}) {
    return (
        <>
            <h2>{headline}</h2>

            {/* Trust elements */}
            <div className="trust-elements">
                <span className="trust-item">
                    <CheckIcon color="#22c55e" />
                    {t('unlock.trust_preview_free')}
                </span>
                <span className="trust-item">
                    <CheckIcon color="#22c55e" />
                    {t('unlock.trust_pay_to_customize')}
                </span>
            </div>

            {/* Promo pricing */}
            <div className="promo-pricing-block">
                <span className="promo-label-small">{t('unlock.one_time_payment')}</span>
                {isEligibleForLaunchPromo ? (
                    <>
                        <div className="promo-price-line">
                            <span className="original-price-strike">{t('unlock.original_trip_price')}</span>
                            <span className="promo-price-large">{t('unlock.promo_price')}</span>
                        </div>
                        <span className="promo-badge">{t('unlock.launch_promo')}</span>
                    </>
                ) : (
                    <div className="promo-price-line">
                        <span className="promo-price-large">{t('unlock.original_trip_price')}</span>
                    </div>
                )}
            </div>

            {/* Features */}
            <ul className="modal-features promo-features">
                <FeatureItem>{t('unlock.bul_1')}</FeatureItem>
                <FeatureItem>{t('unlock.bul_2')}</FeatureItem>
                <FeatureItem>{t('unlock.bul_3')}</FeatureItem>
            </ul>

            {/* No subscription line */}
            <p className="no-subscription-text">{t('unlock.no_subscription')}</p>

            {/* Error messages */}
            {checkoutCanceled && (
                <p className="checkout-error-msg">{t('unlock.payment_canceled')}</p>
            )}
            {sessionExpired && (
                <p className="checkout-error-msg">{t('unlock.session_expired')}</p>
            )}

            {/* CTA - different for guest vs authenticated */}
            {!isAuthenticated ? (
                <button className="modal-cta-btn google-unlock-btn" onClick={onGoogleUnlock}>
                    <GoogleIcon />
                    {t('unlock.cta_google')}
                </button>
            ) : (
                <button className="modal-cta-btn" disabled={loading} onClick={onCheckout}>
                    {loading ? (
                        <span>{t('common.processing')}</span>
                    ) : (
                        <>
                            <SendIcon />
                            {t('unlock.cta_unlock_now')}
                        </>
                    )}
                </button>
            )}

            {/* Secondary action */}
            <button className="modal-text-btn not-now-btn" onClick={onClose}>
                {t('unlock.not_now')}
            </button>
        </>
    );
}

function GuestContent({ t, onGetStarted }: { t: any; onGetStarted: () => void }) {
    return (
        <>
            <h2>{t('unlock.try_free')}</h2>
            <p className="modal-subtitle">{t('unlock.create_account')}</p>

            <ul className="modal-features">
                <FeatureItem>
                    <strong>{t('unlock.free_edits_count')}</strong>
                </FeatureItem>
                <FeatureItem>{t('unlock.features_add_remove')}</FeatureItem>
                <FeatureItem>{t('unlock.features_suggestions')}</FeatureItem>
            </ul>

            <button className="modal-cta-btn" onClick={onGetStarted}>
                <SendIcon />
                {t('unlock.get_started_free')}
            </button>

            <p className="modal-terms">{t('unlock.no_card_required')}</p>
        </>
    );
}

function FreeTrialContent({
    t,
    editsRemaining,
    onUpgradeClick,
    onContinueFree,
    trialMessage
}: {
    t: any;
    editsRemaining: number;
    onUpgradeClick: () => void;
    onContinueFree: () => void;
    trialMessage: string | null;
}) {
    return (
        <>
            <h2>{t('unlock.continue_planning')}</h2>
            <p className="modal-subtitle">
                {t('unlock.remaining_edits', { count: editsRemaining })}
            </p>

            <ul className="modal-features">
                <FeatureItem>{t('unlock.features_custom_ai')}</FeatureItem>
                <FeatureItem warning>{t('unlock.features_premium_pdf')}</FeatureItem>
            </ul>

            <button className="modal-cta-btn" onClick={onUpgradeClick}>
                <LockIcon />
                {t('unlock.upgrade_unlock')}
            </button>

            <div className="modal-secondary-actions">
                <button className="modal-text-btn" onClick={onContinueFree}>
                    {t('unlock.continue_count', { count: editsRemaining })}
                </button>
                {trialMessage && <p className="modal-trial-warning">{trialMessage}</p>}
            </div>
        </>
    );
}

function UpgradeContent({
    t,
    plan,
    onPlanSelect,
    onCheckout,
    loading,
    isTripUnlocked,
    hasTripContext
}: {
    t: any;
    plan: 'trip' | 'annual';
    onPlanSelect: (plan: 'trip' | 'annual') => void;
    onCheckout: () => void;
    loading: boolean;
    isTripUnlocked: boolean;
    hasTripContext: boolean;
}) {
    return (
        <>
            <h2>{t('unlock.unlock_unlimited')}</h2>
            <p className="modal-subtitle">{t('unlock.choose_plan')}</p>

            <div className="plan-selection">
                <button
                    className={`plan-card ${plan === 'annual' ? 'active' : ''}`}
                    onClick={() => onPlanSelect('annual')}
                >
                    <div className="plan-info">
                        <strong>{t('unlock.annual_unlimited')}</strong>
                        <span>{t('unlock.annual_price')}</span>
                    </div>
                    {plan === 'annual' && <CheckIcon />}
                </button>

                {/* Only show "One Trip" option if there's a trip context and it's not already unlocked */}
                {hasTripContext && !isTripUnlocked && (
                    <button
                        className={`plan-card ${plan === 'trip' ? 'active' : ''}`}
                        onClick={() => onPlanSelect('trip')}
                    >
                        <div className="plan-info">
                            <strong>{t('unlock.one_trip')}</strong>
                            <span>{t('unlock.trip_price')}</span>
                        </div>
                        {plan === 'trip' && <CheckIcon />}
                    </button>
                )}
            </div>

            <ul className="modal-features">
                <FeatureItem>{t('unlock.unlimited_ai_edits')}</FeatureItem>
                <FeatureItem>{t('unlock.download_pdf')}</FeatureItem>
                <FeatureItem>{t('unlock.make_private')}</FeatureItem>
            </ul>

            <button className="modal-cta-btn" disabled={loading} onClick={onCheckout}>
                {loading ? (
                    <span>{t('common.processing')}</span>
                ) : (
                    <>
                        <SendIcon />
                        {plan === 'annual' ? t('unlock.unlock_annual_cta') : t('unlock.unlock_trip_cta')}
                    </>
                )}
            </button>

            <p className="modal-terms">{t('unlock.instant_access')}</p>
        </>
    );
}

function FeatureItem({ children, warning }: { children: React.ReactNode; warning?: boolean }) {
    return (
        <li>
            {warning ? <WarningIcon /> : <CheckIcon color="#22c55e" />}
            {children}
        </li>
    );
}

// =============================================================================
// ICONS
// =============================================================================

function CloseIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );
}

function LockIcon() {
    return (
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
    );
}

function CheckIcon({ color = "#22c55e" }: { color?: string }) {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

function WarningIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
    );
}

function SendIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
        </svg>
    );
}

function GoogleIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
    );
}
